// Whether a Beneficial Ownership investigation actually ran its second layer.
//
// The saved ticket used to answer this from `workflowData.second_layer`, which
// is only the intake question's answer held in component state. A background
// run ("run it and email me") turns the second layer on server-side without
// that state ever changing, so the ticket and the PDF both reported "Second
// Layer: off" for runs that demonstrably went two levels deep.
//
// The workflow state saved alongside the ticket already knows the truth three
// different ways, so read it from there instead of trusting the intake answer:
//   - `second_layer_requested` / `enforce_second_layer` — the flags the server
//     sets when it decides to run the layer.
//   - `pipeline.second_layer_results.entities[]` — results only exist if the
//     layer ran, which makes this the strongest evidence of the three.
//   - `pipeline.third_layer_results` — a third layer implies a second.
//
// Deriving it at read time (rather than only fixing the save) also corrects
// tickets that were already saved with the wrong boolean.

const asArray = (value: any): any[] => (Array.isArray(value) ? value : []);

const layerHasResults = (layer: any): boolean =>
  asArray(layer?.entities).length > 0;

export const didSecondLayerRunFromState = (
  workflowState: any,
  fallback?: any,
): boolean => {
  const state = workflowState || {};
  const pipeline = state?.pipeline || {};

  return Boolean(
    state?.second_layer_requested ||
      state?.enforce_second_layer ||
      layerHasResults(pipeline?.second_layer_results) ||
      layerHasResults(pipeline?.third_layer_results) ||
      fallback,
  );
};

export const didSecondLayerRun = (ticket: any): boolean => {
  const workflowData = Array.isArray(ticket?.workflowData)
    ? ticket.workflowData[0]
    : ticket?.workflowData;
  const data = workflowData || {};

  return didSecondLayerRunFromState(data?.workflow_state, data?.second_layer);
};

// A third layer is reported separately because background runs deliberately
// exclude it, so "second ran" and "third ran" are genuinely different answers.
export const didThirdLayerRun = (ticket: any): boolean => {
  const workflowData = Array.isArray(ticket?.workflowData)
    ? ticket.workflowData[0]
    : ticket?.workflowData;
  const pipeline = (workflowData || {})?.workflow_state?.pipeline || {};
  return layerHasResults(pipeline?.third_layer_results);
};
